// src/Billing.js — AVS Cloud | Billing & Speicherverwaltung
// §312j BGB Bestellbutton | §356 Abs.5 BGB Widerrufsrecht-Checkbox | §312k BGB Kündigungsbutton
import BodyScrollLock from './components/BodyScrollLock';
import React, { useState, useEffect, useCallback } from 'react';
import BillingAddressForm from './components/BillingAddressForm';

// Preis-Berechnungshelfer
const PRICE_PER_10GB_CENTS = 40; // 0,40 €

function calcMonthlyPrice(storageGb) {
  return (storageGb / 10) * PRICE_PER_10GB_CENTS;
}

function formatEur(cents) {
  return (cents / 100).toFixed(2).replace('.', ',') + ' €';
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// §356 Abs.5 BGB — Exakter Pflichttext
const WIDERRUF_CHECKBOX_TEXT =
  'Ich stimme ausdrücklich zu, dass Sie mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist beginnen. Ich habe zur Kenntnis genommen, dass ich mein Widerrufsrecht verliere, sobald der Vertrag vollständig erfüllt worden ist.';

const s = {
  page: { minHeight: '100vh', padding: '2rem clamp(1rem, 4vw, 2.5rem) 5rem' },
  inner: { maxWidth: '720px', margin: '0 auto' },
  back: { display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', textDecoration: 'none', marginBottom: '2.5rem', cursor: 'pointer' },
  title: { fontFamily: 'var(--font-head)', fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.04em', color: 'var(--white)', marginBottom: '0.4rem', lineHeight: 1.1 },
  subtitle: { fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '2.5rem', paddingBottom: '2rem', borderBottom: '1px solid var(--border)' },
  section: { marginBottom: '2.5rem', paddingBottom: '2.5rem', borderBottom: '1px solid var(--border)' },
  sectionLast: { marginBottom: '2.5rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 700, color: 'var(--cyan)', fontFamily: 'var(--font-head)', marginBottom: '1.25rem', letterSpacing: '-0.01em' },
  card: { background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1.25rem 1.5rem' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' },
  label: { fontSize: '0.82rem', color: 'var(--muted)' },
  value: { fontSize: '0.88rem', color: 'var(--white)', fontWeight: 600 },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' },
  badgeActive: { background: 'rgba(74, 222, 128, 0.15)', color: '#4ade80', border: '1px solid rgba(74, 222, 128, 0.3)' },
  badgeCancelled: { background: 'rgba(255, 200, 0, 0.1)', color: '#fbbf24', border: '1px solid rgba(255, 200, 0, 0.3)' },
  warningBox: { background: 'rgba(255, 200, 0, 0.06)', border: '1px solid rgba(255, 200, 0, 0.25)', borderRadius: '8px', padding: '0.85rem 1rem', marginTop: '0.75rem', fontSize: '0.82rem', color: '#fbbf24' },
  dangerBtn: { padding: '0.65rem 1.25rem', background: 'transparent', border: '1px solid rgba(255, 80, 80, 0.4)', borderRadius: '7px', color: '#ff8888', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', marginTop: '1rem', fontFamily: 'inherit' },
  // Slider
  sliderWrap: { marginTop: '1.25rem' },
  slider: { width: '100%', cursor: 'pointer', accentColor: 'var(--cyan)', marginBottom: '0.5rem' },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: 'var(--muted)' },
  storagePicker: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' },
  pickerBtn: { width: '36px', height: '36px', borderRadius: '6px', background: 'rgba(255,255,255,0.07)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' },
  storageVal: { flex: 1, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 700, color: 'var(--white)' },
  storageUnit: { fontSize: '0.82rem', color: 'var(--muted)' },
  // Checkout Box (§312j BGB Pflichtangaben)
  checkoutBox: { background: 'rgba(0, 212, 255, 0.04)', border: '1px solid rgba(0, 212, 255, 0.25)', borderRadius: '10px', padding: '1.25rem 1.5rem', marginTop: '1.25rem' },
  checkoutTitle: { fontSize: '0.72rem', fontWeight: 700, color: 'var(--cyan)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem' },
  checkoutRow: { display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' },
  checkoutKey: { color: 'var(--muted)' },
  checkoutVal: { color: 'var(--white)', fontWeight: 600, textAlign: 'right' },
  divider: { height: '1px', background: 'var(--border)', margin: '0.75rem 0' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' },
  totalKey: { color: 'var(--muted)', fontWeight: 600 },
  totalVal: { color: 'var(--cyan)', fontWeight: 800, fontFamily: 'var(--font-mono)' },
  // Checkboxen
  checkboxWrap: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginTop: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '8px' },
  checkbox: { width: '18px', height: '18px', flexShrink: 0, marginTop: '2px', accentColor: 'var(--cyan)', cursor: 'pointer' },
  checkboxLabel: { fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.6 },
  link: { color: 'var(--cyan)', textDecoration: 'underline', textUnderlineOffset: '3px' },
  // Order Buttons
  btnPrimary: { display: 'block', width: '100%', padding: '0.9rem 1.5rem', borderRadius: '8px', fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', letterSpacing: '0.02em', marginTop: '0.75rem', border: 'none', transition: 'opacity 0.2s' },
  btnStripe: { background: 'var(--cyan)', color: '#0a0a0a' },
  btnPaypal: { background: '#ffc439', color: '#000' },
  btnDisabled: { opacity: 0.35, cursor: 'not-allowed' },
  // Rechnungstabelle
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' },
  th: { textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--cyan)', fontWeight: 600, borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase' },
  td: { padding: '0.6rem 0.75rem', color: 'var(--muted)', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' },
  errorMsg: { background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.25)', borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '1rem', fontSize: '0.82rem', color: '#ff8888' },
  successMsg: { background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: '8px', padding: '0.75rem 1rem', marginTop: '1rem', fontSize: '0.82rem', color: '#4ade80' },
  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#131316', border: '1px solid var(--border)', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '100%', margin: 'auto 0', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { fontFamily: 'var(--font-head)', fontSize: '1.1rem', fontWeight: 800, color: 'var(--white)', marginBottom: '1rem' },
  modalText: { fontSize: '0.85rem', color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' },
  modalActions: { display: 'flex', gap: '0.75rem' },
  modalCancel: { flex: 1, padding: '0.7rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: '7px', color: 'var(--muted)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  modalConfirm: { flex: 1, padding: '0.7rem', background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.4)', borderRadius: '7px', color: '#ff8888', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-head)' },
};

export default function Billing({ token }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Checkout State
  const [storageGb, setStorageGb] = useState(10);
  const [widerrufChecked, setWiderrufChecked] = useState(false);
  const [agbChecked, setAgbChecked] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutSuccess, setCheckoutSuccess] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [setupFeeRequired, setSetupFeeRequired] = useState(false);
  const [setupFeeCents, setSetupFeeCents] = useState(0);

  // Rechnungsadresse
  const [showAddressForm, setShowAddressForm] = useState(false);

  // Kündigungsmodal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');

  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, subRes, invRes, setupRes] = await Promise.all([
        fetch('/api/users/profile', { headers: authHeaders }),
        fetch('/api/billing/subscription', { headers: authHeaders }),
        fetch('/api/billing/invoices', { headers: authHeaders }),
        fetch('/api/billing/setup-fee-check', { headers: authHeaders }),
      ]);

      if (profileRes.ok) {
        const d = await profileRes.json();
        if (d.success) setUser(d.user);
      }
      if (subRes.ok) {
        const d = await subRes.json();
        if (d.success) setSubscription(d.subscription);
      }
      if (invRes.ok) {
        const d = await invRes.json();
        if (d.success) setInvoices(d.invoices || []);
      }
      if (setupRes.ok) {
        const d = await setupRes.json();
        if (d.success) {
          setSetupFeeRequired(d.setup_fee_required);
          setSetupFeeCents(d.setup_fee_cents || 0);
        }
      }
    } catch (err) {
      setError('Fehler beim Laden der Daten.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // PayPal Return-URL: Agreement nach Zustimmung aktivieren
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ppToken = params.get('token'); // EC-XXXXXXXXX
    const success  = params.get('success');
    const ppSuccess = params.get('paypal_success');
    const ppCancelled = params.get('paypal_cancelled');

    if (ppCancelled) {
      setCheckoutError('PayPal-Zahlung abgebrochen.');
      window.history.replaceState({}, '', '/billing');
      return;
    }

    if ((ppSuccess || ppToken) && ppToken) {
      // Agreement beim Backend aktivieren
      fetch(`/api/billing/paypal/execute?token=${ppToken}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(d => {
          if (d.success) {
            setCheckoutSuccess('PayPal-Abonnement erfolgreich aktiviert! 🎉');
            fetchData();
          } else {
            setCheckoutError(d.error || 'Fehler beim Aktivieren des PayPal-Abonnements.');
          }
        })
        .catch(() => setCheckoutError('Netzwerkfehler beim Aktivieren.'));
      window.history.replaceState({}, '', '/billing');
    }

    if (success === '1') {
      setCheckoutSuccess('Zahlung erfolgreich! Dein Abo wurde aktiviert. 🎉');
      fetchData();
      window.history.replaceState({}, '', '/billing');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyCents = calcMonthlyPrice(storageGb);
  const canOrder = widerrufChecked && agbChecked && !checkoutLoading;

  const handleSaveAddress = async (formData) => {
    const res = await fetch('/api/billing/address', {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify(formData),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || 'Fehler');
    // Refresh user
    await fetchData();
    setShowAddressForm(false);
  };

  const handleOrder = async (provider) => {
    if (!canOrder) return;
    setCheckoutLoading(true);
    setCheckoutError('');
    setCheckoutSuccess('');

    try {
      const endpoint =
        provider === 'stripe'
          ? '/api/billing/subscribe'
          : '/api/billing/subscribe/paypal';

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          storage_gb: storageGb,
          consent_widerruf: widerrufChecked,
          consent_agb: agbChecked,
          consent_text: WIDERRUF_CHECKBOX_TEXT,
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setCheckoutError(data.error || 'Fehler beim Erstellen der Bestellung.');
        return;
      }

      // Downgrade — kein Checkout, nur Meldung
      if (data.downgrade) {
        if (data.storage_warning) {
          // Warnung prominent anzeigen
          setCheckoutError(data.message);
        } else {
          setCheckoutSuccess(data.message);
        }
        await fetchData();
        return;
      }

      // Reaktivierung
      if (data.reactivated) {
        setCheckoutSuccess(data.message);
        await fetchData();
        return;
      }

      if (provider === 'stripe' && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (provider === 'paypal' && data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        setCheckoutSuccess('Bestellung erfolgreich erstellt. Bitte prüfe deine E-Mails.');
        await fetchData();
      }
    } catch (err) {
      setCheckoutError('Netzwerkfehler. Bitte versuche es erneut.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    setCancelError('');
    try {
      const res = await fetch('/api/billing/subscription', {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setCancelError(data.error || 'Fehler bei der Kündigung.');
        return;
      }
      setShowCancelModal(false);
      await fetchData();
    } catch (err) {
      setCancelError('Netzwerkfehler.');
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.inner}>
          <p style={{ color: 'var(--muted)', fontSize: '0.88rem' }}>Laden…</p>
        </div>
      </div>
    );
  }

  const hasBillingAddress = user?.billing_address_set;
  const isSubscribed = subscription && subscription.status === 'active';
  const isCancelling = subscription?.cancel_at_period_end;

  // Widerrufsbutton anzeigen wenn Abo vorhanden und innerhalb 14 Tage
  const showWiderrufsBtn = isSubscribed && (() => {
    if (!subscription?.created_at) return false;
    const created = new Date(subscription.created_at);
    const diffDays = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays <= 14;
  })();

  return (
    <div style={s.page}>
      <div style={s.inner}>
        <a href="/settings" style={s.back}>← Zurück zu Einstellungen</a>
        <h1 style={s.title}>Speicher & Abonnement</h1>
        <p style={s.subtitle}>Verwalte dein Speicherlimit, Abonnement und Rechnungen.</p>

        {error && <div style={s.errorMsg}>{error}</div>}

        {/* SEKTION 1: Aktueller Plan */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Aktueller Plan</h2>
          <div style={s.card}>
            <div style={s.row}>
              <span style={s.label}>Plan</span>
              <span style={s.value}>
                {isSubscribed
                  ? `${subscription.storage_gb} GB — ${formatEur(subscription.price_cents)}/Monat`
                  : '5 GB Gratis'}
              </span>
            </div>
            {isSubscribed && (
              <>
                <div style={s.row}>
                  <span style={s.label}>Status</span>
                  <span
                    style={{
                      ...s.badge,
                      ...(isCancelling ? s.badgeCancelled : s.badgeActive),
                    }}
                  >
                    {isCancelling ? 'Kündigung ausstehend' : 'Aktiv'}
                  </span>
                </div>
                <div style={s.row}>
                  <span style={s.label}>
                    {isCancelling ? 'Läuft ab am' : 'Nächste Abbuchung'}
                  </span>
                  <span style={s.value}>
                    {formatDate(subscription.current_period_end)}
                  </span>
                </div>
                {!!isCancelling && (
                  <div style={s.warningBox}>
                    ⚠️ Dein Abo läuft am {formatDate(subscription.current_period_end)} aus.
                    Danach wird dein Speicherlimit auf 5 GB (Kostenlos) zurückgesetzt.
                    <br /><br />
                    Du kannst bis dahin jederzeit upgraden oder deinen aktuellen Plan reaktivieren.
                  </div>
                )}
                {!isCancelling && (
                  <button
                    style={s.dangerBtn}
                    onClick={() => setShowCancelModal(true)}
                  >
                    Auf kostenlosen Plan (5 GB) wechseln
                  </button>
                )}
              </>
            )}
          </div>

          {/* §356a BGB Widerrufsbutton */}
          {showWiderrufsBtn && (
            <div style={{ ...s.card, marginTop: '1rem', borderColor: 'rgba(0, 212, 255, 0.2)' }}>
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)', marginBottom: '0.75rem' }}>
                Du hast das Recht, diesen Vertrag innerhalb von 14 Tagen zu widerrufen (§ 355 BGB).
              </p>
              <a
                href="/widerruf"
                style={{
                  display: 'inline-block',
                  padding: '0.6rem 1rem',
                  background: 'rgba(0,212,255,0.08)',
                  border: '1px solid rgba(0,212,255,0.3)',
                  borderRadius: '6px',
                  color: 'var(--cyan)',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                }}
              >
                Vertrag widerrufen →
              </a>
            </div>
          )}
        </div>

        {/* SEKTION 2: Speicher erweitern */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Speicher erweitern</h2>

          {/* Rechnungsadresse */}
          {!hasBillingAddress && (
            <div style={{ ...s.warningBox, marginBottom: '1.25rem' }}>
              ⚠️ Vor der Buchung muss eine Rechnungsadresse hinterlegt werden (§ 14 UStG).{' '}
              <button
                style={{ background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}
                onClick={() => setShowAddressForm(true)}
              >
                Adresse hinterlegen
              </button>
            </div>
          )}

          {hasBillingAddress && !showAddressForm && (
            <div style={{ ...s.card, marginBottom: '1.25rem', fontSize: '0.82rem', color: 'var(--muted)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: 'var(--white)', fontWeight: 600 }}>{user?.billing_firstname} {user?.billing_lastname}</span><br />
                  {user?.billing_street}, {user?.billing_zip} {user?.billing_city}
                </div>
                <span style={{ fontSize: '0.72rem', color: 'var(--muted)', opacity: 0.6 }}>
                  Änderung nur per Admin
                </span>
              </div>
            </div>
          )}

          {showAddressForm && (
            <div style={{ ...s.card, marginBottom: '1.25rem' }}>
              <BillingAddressForm
                initialData={user || {}}
                onSave={handleSaveAddress}
                onCancel={() => setShowAddressForm(false)}
                showTitle={true}
              />
            </div>
          )}

          {/* Speicher-Slider */}
          <div style={s.sliderWrap}>
            <div style={s.storagePicker}>
              <button
                style={s.pickerBtn}
                onClick={() => setStorageGb(Math.max(10, storageGb - 10))}
                disabled={storageGb <= 10}
              >−</button>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <span style={s.storageVal}>{storageGb}</span>
                <span style={s.storageUnit}> GB</span>
              </div>
              <button
                style={s.pickerBtn}
                onClick={() => setStorageGb(Math.min(250, storageGb + 10))}
                disabled={storageGb >= 250}
              >+</button>
            </div>
            <input
              type="range"
              min={10}
              max={250}
              step={10}
              value={storageGb}
              onChange={(e) => setStorageGb(Number(e.target.value))}
              style={s.slider}
            />
            <div style={s.sliderLabels}>
              <span>10 GB</span>
              <span>250 GB</span>
            </div>
          </div>

          {/* §312j Abs.2 BGB Pflichtangaben-Box */}
          <div style={s.checkoutBox}>
            <div style={s.checkoutTitle}>Bestellübersicht (§ 312j Abs. 2 BGB)</div>

            {/* Speicher nach Abschluss */}
            <div style={{ background: 'rgba(0,212,255,0.06)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '0.75rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Dein Speicher nach Abschluss</div>
              <div style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--cyan)', fontFamily: 'var(--font-mono)' }}>
                {5 + storageGb} GB
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>5 GB Basis + {storageGb} GB Abo</div>
            </div>

            {/* Downgrade-Hinweis (aktiv, nicht gekündigt) */}
            {isSubscribed && !isCancelling && storageGb < subscription.storage_gb && (
              <div style={{ background: 'rgba(255,200,0,0.06)', border: '1px solid rgba(255,200,0,0.25)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#fbbf24' }}>
                ⚠️ Downgrade: Dein aktuelles {subscription.storage_gb} GB Abo läuft bis {formatDate(subscription.current_period_end)}. Danach wechselst du auf {storageGb} GB.
              </div>
            )}

            {/* Upgrade-Hinweis (aktiv, nicht gekündigt) */}
            {isSubscribed && !isCancelling && storageGb > subscription.storage_gb && (
              <div style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#4ade80' }}>
                ↑ Upgrade: Dein {subscription.storage_gb} GB Abo wird sofort ersetzt durch {storageGb} GB.
              </div>
            )}

            {/* Gekündigt: Reaktivierung */}
            {isSubscribed && isCancelling && storageGb === subscription.storage_gb && (
              <div style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.25)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: 'var(--cyan)' }}>
                ↩ Dein {subscription.storage_gb} GB Abo wird reaktiviert. Keine neue Zahlung — dein bestehendes Abo läuft weiter.
              </div>
            )}

            {/* Gekündigt: Upgrade */}
            {isSubscribed && isCancelling && storageGb > subscription.storage_gb && (
              <div style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.25)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#4ade80' }}>
                ↑ Upgrade: Dein gekündigtes {subscription.storage_gb} GB Abo wird sofort ersetzt durch {storageGb} GB.
              </div>
            )}

            {/* Gekündigt: Downgrade auf anderen Plan */}
            {isSubscribed && isCancelling && storageGb < subscription.storage_gb && (
              <div style={{ background: 'rgba(255,200,0,0.06)', border: '1px solid rgba(255,200,0,0.25)', borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '0.75rem', fontSize: '0.82rem', color: '#fbbf24' }}>
                ⚠️ Dein {subscription.storage_gb} GB Abo läuft bis {formatDate(subscription.current_period_end)} weiter. Danach kannst du den {storageGb} GB Plan buchen.
              </div>
            )}

            <div style={s.checkoutRow}>
              <span style={s.checkoutKey}>Leistung</span>
              <span style={s.checkoutVal}>AVS Cloud Speicher — {storageGb} GB</span>
            </div>
            <div style={s.checkoutRow}>
              <span style={s.checkoutKey}>Monatlicher Preis</span>
              <span style={s.checkoutVal}>
                {formatEur(monthlyCents)}/Monat
                <span style={{ fontSize: '0.75rem', color: 'var(--muted)', display: 'block', textAlign: 'right' }}>
                  (inkl. aller Abgaben, keine USt. gem. § 19 UStG)
                </span>
              </span>
            </div>

            {/* Einrichtungsgebühr */}
            {setupFeeRequired && (
              <div style={s.checkoutRow}>
                <span style={s.checkoutKey}>Einrichtungsgebühr (einmalig)</span>
                <span style={s.checkoutVal}>{formatEur(setupFeeCents)}</span>
              </div>
            )}

            <div style={s.checkoutRow}>
              <span style={s.checkoutKey}>Laufzeit</span>
              <span style={s.checkoutVal}>Monatlich, automatische Verlängerung</span>
            </div>
            <div style={s.checkoutRow}>
              <span style={s.checkoutKey}>Kündigung</span>
              <span style={s.checkoutVal}>Jederzeit zum Monatsende möglich</span>
            </div>
            <div style={s.divider} />

            {/* Gesamtbetrag — abhängig vom Szenario */}
            {(() => {
              const isDowngrade = isSubscribed && storageGb < subscription?.storage_gb;
              const isReactivation = isSubscribed && isCancelling && storageGb === subscription?.storage_gb;
              if (isReactivation) return (
                <div style={s.totalRow}>
                  <span style={s.totalKey}>Heute fällig</span>
                  <span style={{ ...s.totalVal, color: '#4ade80' }}>0,00 € (Reaktivierung)</span>
                </div>
              );
              if (isDowngrade) return (
                <div style={s.totalRow}>
                  <span style={s.totalKey}>Heute fällig</span>
                  <span style={{ ...s.totalVal, color: '#4ade80' }}>0,00 € (zum Monatsende)</span>
                </div>
              );
              return (
                <div style={s.totalRow}>
                  <span style={s.totalKey}>Gesamtbetrag heute</span>
                  <span style={s.totalVal}>{formatEur(monthlyCents + (setupFeeRequired ? setupFeeCents : 0))}</span>
                </div>
              );
            })()}

            <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
              <a href="/agb" target="_blank" rel="noopener noreferrer" style={s.link}>AGB</a>
              {' '}&middot;{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={s.link}>Datenschutz</a>
            </div>
          </div>

          {/* §356 Abs.5 BGB Widerrufs-Checkbox — SEPARATE, NICHT VORAUSGEFÜLLT */}
          <div style={s.checkboxWrap}>
            <input
              type="checkbox"
              id="widerruf_checkbox"
              style={s.checkbox}
              checked={widerrufChecked}
              onChange={(e) => setWiderrufChecked(e.target.checked)}
            />
            <label htmlFor="widerruf_checkbox" style={s.checkboxLabel}>
              {WIDERRUF_CHECKBOX_TEXT}
            </label>
          </div>

          {/* AGB-Checkbox — SEPARATE */}
          <div style={s.checkboxWrap}>
            <input
              type="checkbox"
              id="agb_checkbox"
              style={s.checkbox}
              checked={agbChecked}
              onChange={(e) => setAgbChecked(e.target.checked)}
            />
            <label htmlFor="agb_checkbox" style={s.checkboxLabel}>
              Ich akzeptiere die{' '}
              <a href="/agb" target="_blank" rel="noopener noreferrer" style={s.link}>AGB</a>{' '}
              und habe die{' '}
              <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={s.link}>
                Datenschutzerklärung
              </a>{' '}
              zur Kenntnis genommen.
            </label>
          </div>

          {checkoutError && <div style={s.errorMsg}>{checkoutError}</div>}
          {checkoutSuccess && <div style={s.successMsg}>{checkoutSuccess}</div>}

          {/* §312j BGB Bestellbuttons — State-abhängig */}
          {(() => {
            const subGb = subscription?.storage_gb;
            const isSamePlan = isSubscribed && !isCancelling && storageGb === subGb;
            const isReactivation = isSubscribed && isCancelling && storageGb === subGb;
            const isDowngrade = isSubscribed && storageGb < subGb;
            const isUpgrade = isSubscribed && storageGb > subGb;
            const needsPayment = !isSubscribed || isUpgrade;

            if (isSamePlan) return (
              <div style={{ ...s.warningBox, textAlign: 'center', marginTop: '0.75rem' }}>
                Du hast bereits den {storageGb} GB Plan.
              </div>
            );

            if (isReactivation) return (
              <button
                style={{
                  ...s.btnPrimary,
                  background: 'rgba(0,212,255,0.15)', color: 'var(--cyan)', border: '1px solid rgba(0,212,255,0.4)',
                  ...(!canOrder || !hasBillingAddress ? s.btnDisabled : {}),
                }}
                onClick={() => handleOrder('stripe')}
                disabled={!canOrder || !hasBillingAddress}
              >
                ↩ {subscription.storage_gb} GB Plan reaktivieren
              </button>
            );

            if (isDowngrade && isCancelling) return (
              <button
                style={{
                  ...s.btnPrimary,
                  background: 'rgba(255,200,0,0.1)', color: '#fbbf24', border: '1px solid rgba(255,200,0,0.3)',
                  ...(!canOrder || !hasBillingAddress ? s.btnDisabled : {}),
                }}
                onClick={() => handleOrder('stripe')}
                disabled={!canOrder || !hasBillingAddress}
              >
                ↓ Wechsel auf {storageGb} GB vormerken
              </button>
            );

            if (isDowngrade) return (
              <button
                style={{
                  ...s.btnPrimary,
                  background: 'rgba(255,200,0,0.1)', color: '#fbbf24', border: '1px solid rgba(255,200,0,0.3)',
                  ...(!canOrder || !hasBillingAddress ? s.btnDisabled : {}),
                }}
                onClick={() => handleOrder('stripe')}
                disabled={!canOrder || !hasBillingAddress}
              >
                ↓ Auf {storageGb} GB wechseln (zum Monatsende)
              </button>
            );

            // Neue Bestellung oder Upgrade: Stripe + PayPal
            return (
              <>
                <button
                  style={{
                    ...s.btnPrimary,
                    ...s.btnStripe,
                    ...(!canOrder || !hasBillingAddress ? s.btnDisabled : {}),
                  }}
                  onClick={() => handleOrder('stripe')}
                  disabled={!canOrder || !hasBillingAddress}
                >
                  💳 Zahlungspflichtig bestellen (Stripe)
                </button>
                <button
                  style={{
                    ...s.btnPrimary,
                    ...s.btnPaypal,
                    ...(!canOrder || !hasBillingAddress ? s.btnDisabled : {}),
                  }}
                  onClick={() => handleOrder('paypal')}
                  disabled={!canOrder || !hasBillingAddress}
                >
                  🅿️ Zahlungspflichtig bestellen (PayPal)
                </button>
              </>
            );
          })()}

          {(!widerrufChecked || !agbChecked) && (
            <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.5rem', textAlign: 'center' }}>
              Bitte beide Checkboxen ankreuzen, um fortzufahren.
            </p>
          )}
        </div>

        {/* SEKTION 3: Rechnungshistorie */}
        <div style={s.sectionLast}>
          <h2 style={s.sectionTitle}>Rechnungshistorie</h2>
          {invoices.length === 0 ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
              Noch keine Rechnungen vorhanden.
            </p>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Datum</th>
                  <th style={s.th}>Nummer</th>
                  <th style={s.th}>Zeitraum</th>
                  <th style={s.th}>GB</th>
                  <th style={s.th}>Betrag</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td style={s.td}>{formatDate(inv.created_at)}</td>
                    <td style={{ ...s.td, fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>
                      {inv.invoice_number}
                    </td>
                    <td style={s.td}>
                      {formatDate(inv.period_start)} – {formatDate(inv.period_end)}
                    </td>
                    <td style={s.td}>{inv.storage_gb}</td>
                    <td style={{ ...s.td, fontWeight: 600, color: 'var(--white)' }}>
                      {formatEur(inv.amount_cents)}
                    </td>
                    <td style={s.td}>{inv.status}</td>
                    <td style={s.td}>
                      {inv.pdf_path ? (
                        <a
                          href={`/api/billing/invoices/${inv.id}/pdf?token=${encodeURIComponent(token)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={s.link}
                        >
                          PDF
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Kündigungsmodal (§312k BGB — zweistufig) */}
      {showCancelModal && (
        <div style={s.overlay} onClick={() => setShowCancelModal(false)}>
          <BodyScrollLock />
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <p style={s.modalTitle}>Auf kostenlosen Plan wechseln</p>
            <p style={s.modalText}>
              Möchtest du dein Abo kündigen und zum kostenlosen 5 GB Plan wechseln?<br /><br />
              Dein aktueller Speicher ({subscription?.storage_gb} GB) bleibt bis zum Ende des Abrechnungszeitraums ({formatDate(subscription?.current_period_end)}) aktiv.
              Danach wird dein Speicherlimit auf 5 GB zurückgesetzt.
              <br /><br />
              Du kannst bis dahin jederzeit upgraden oder deinen Plan reaktivieren.
            </p>
            {cancelError && <div style={{ ...s.errorMsg, marginBottom: '1rem' }}>{cancelError}</div>}
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setShowCancelModal(false)}>
                Abbrechen
              </button>
              <button
                style={{ ...s.modalConfirm, opacity: cancelLoading ? 0.5 : 1 }}
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
              >
                {cancelLoading ? 'Wird gekündigt…' : 'Auf 5 GB wechseln'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
